import React, { useCallback, useRef, useState } from 'react';
import cx from 'classnames';

import Button from '../../Button';

import DatePicker from './DatePicker';
import TimeInput from './TimeInput';
import TimePicker from './TimePicker';
import { formatTime } from './functions';
import type { DateTimeValueT } from './types';

interface SelectPropsT {
  value: Date | undefined;
  onChange: (date: Date) => void;
  picker: 'date' | 'time' | 'date-time';
  onlyDate: boolean | undefined;
  onConfirm: (date: Date) => void;
  onCancel: () => void;
}

const Select = React.memo(
  ({
    value,
    onChange,
    picker,
    onlyDate,
    onConfirm,
    onCancel,
  }: SelectPropsT) => {
    const [showTimer, setShowTimer] = useState<boolean>(picker !== 'date');
    const [formattedValue, setFormattedValue] = useState<DateTimeValueT>({
      year: (value ? new Date(value) : new Date()).getFullYear(),
      month: (value ? new Date(value) : new Date()).getMonth(),
      date: value ? new Date(value).getDate() : undefined,
      time: formatTime(value ? new Date(value) : new Date()),
    });

    const footerRef = useRef<HTMLDivElement | null>(null);

    const handleSelectDate = useCallback(
      (year: number, month: number, date: number) => {
        setFormattedValue((value) => ({
          ...value,
          year,
          month,
          date,
        }));

        if (footerRef.current.clientHeight === 0) {
          const splitTime = formattedValue.time.split(':');
          onConfirm(
            new Date(
              year,
              month,
              date,
              parseInt(splitTime[0]),
              parseInt(splitTime[1])
            )
          );
        }
      },
      [footerRef.current, formattedValue]
    );

    const handleTime = useCallback(
      (change: boolean) => (time: string) => {
        setFormattedValue((value) => ({
          ...value,
          time,
        }));

        if (change) {
          const splitTime = time.split(':');
          const newDate = new Date(
            formattedValue.year,
            formattedValue.month,
            formattedValue.date ?? new Date().getDate(),
            parseInt(splitTime[0]),
            parseInt(splitTime[1])
          );
          onChange(newDate);
        }
      },
      [formattedValue]
    );

    return (
      <>
        <div className={cx('', { invisible: showTimer })}>
          <DatePicker
            year={formattedValue.year}
            month={formattedValue.month}
            date={formattedValue.date}
            onSelect={handleSelectDate}
          />
        </div>

        {showTimer && (
          <TimePicker
            value={formattedValue.time ?? formatTime(new Date())}
            onChange={handleTime(false)}
          />
        )}

        {!onlyDate && (
          <TimeInput value={formattedValue.time} onChange={handleTime(true)} />
        )}

        <div
          className="flex md:hidden justify-end gap-2.5 w-full px-5 pb-5"
          ref={footerRef}
        >
          <Button className="font-bold border-0 h-10" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            className="font-bold w-14 h-10"
            variant="solid"
            disabled={formattedValue.date === undefined}
            onClick={() => {
              if (!onlyDate && !showTimer) {
                setShowTimer(true);
              } else {
                const splitTime = formattedValue.time.split(':');
                const newDate = new Date(
                  formattedValue.year,
                  formattedValue.month,
                  formattedValue.date,
                  parseInt(splitTime[0]),
                  parseInt(splitTime[1])
                );
                onConfirm(newDate);
              }
            }}
          >
            Ok
          </Button>
        </div>
      </>
    );
  }
);

export default Select;
